import { chain, flatMap, get } from 'lodash';
import { useSelector } from 'react-redux';
import { userSelector } from 'app/selectors';
import StyledForm from './StyledForm';
import { useForm } from 'react-hook-form';
import CustomInput from 'components/fields/input2';
import Button from 'components/Button';
import MySelect from 'components/MySelect';
import { useEffect, useMemo, useState } from 'react';
import { handleError, request } from 'app/api';
import { CircularProgress, IconButton, Skeleton } from '@mui/material';
import { useContext } from 'react';
import TableRow from 'components/table/CustomTableRow';
import TableCol from 'components/table/CustomTableCol';
import { LanguageContext } from 'components/language';
import { useApi, useDialog, useMessage } from 'app/hooks';
import { toast } from 'react-toastify';
import { ReactComponent as EditIcon } from 'assets/img/Edit_light.svg';
import { ReactComponent as TrashIcon } from 'assets/img/Trash_light.svg';
import StyledModal from './StyledUpdateModal';

const initialValues = {
    rateId: null,
    bookingComRateId: '',
    bookingComRateName: '',
};

const RatesIntegration = () => {
    const user = useSelector(userSelector);
    const [{ roomsterRates, bookingComRates }, setData] = useState({});
    const [loading, setLoading] = useState(false);
    const { t } = useContext(LanguageContext);
    const activeHotelId = get(user, 'activeHotelId');
    const [uploading, setUploading] = useState(false);
    const { setMessage } = useMessage();
    const [isOpen, handleShow, hide] = useDialog();
    const [activeBookingComRate, setActiveBookingComRate] = useState(null);

    const { data: rates = [] } = useApi(`rate/get-all/${localStorage.locale}`);

    const allRates = useMemo(() => {
        return flatMap(rates, (rate) => [rate, ...rate.rateDiscounts]);
    }, [rates]);

    const { handleSubmit, control, reset } = useForm({ defaultValues: initialValues });

    const connectedRates = useMemo(() => {
        return chain(roomsterRates)
            .filter((item) => item?.bookingComRateId)
            .keyBy('bookingComRateId')
            .value();
    }, [roomsterRates]);

    const notConnectedRates = useMemo(() => {
        return roomsterRates?.filter((item) => !item?.bookingComRateId);
    }, [roomsterRates]);

    const fetchData = () => {
        setLoading(true);
        request({
            url: `/booking-com/rates?hotelId=${activeHotelId}`,
            success: ({ data }) => {
                if (data) setData(data);
                setLoading(false);
            },
            fail: () => {
                setLoading(false);
            },
        });
    };

    const handleDelete = (rateId) => {
        setMessage({
            text: 'Are you sure you want to permanently reconnect the rate?',
            confirm: true,

            yes: () => {
                request({
                    method: 'put',
                    url: `booking-com/rate/disconnect?rateId=${rateId}`,
                    success: () => {
                        setMessage({});
                        fetchData();
                    },
                    fail: (e) => handleError(e),
                });
            },
            no: () => {
                setMessage({});
            },
        });
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeHotelId]);

    const onSubmit = (data) => {
        setUploading(true);
        request({
            url: `/booking-com/rate`,
            method: 'post',
            params: data,
            success: () => {
                reset(initialValues);
                toast.success(t('RATE_CONNECTED_SUCCESSFULLY'));
                fetchData();
            },
            fail: handleError,
            finish: () => {
                setUploading(false);
            },
        });
    };

    return (
        <div className="main-table-container">
            <StyledForm onSubmit={handleSubmit(onSubmit)}>
                <MySelect
                    nullable={false}
                    control={control}
                    name="rateId"
                    options={allRates}
                    isSearchable
                    isClearable
                    sx={{ width: '200px' }}
                    placeholder={t('RATE')}
                />
                <CustomInput control={control} name="bookingComRateId" placeholder={t('BOOKING_COM_RATE_ID')} />
                <CustomInput control={control} name="bookingComRateName" placeholder={t('BOOKING_COM_RATE_NAME')} />
                <Button type="submit" disabled={uploading}>
                    {uploading ? <CircularProgress size={20} /> : t('SAVE')}
                </Button>
            </StyledForm>

            <div className="category-body">
                <TableRow head>
                    <TableCol width={30}>#</TableCol>
                    <TableCol>{t('NAME')}</TableCol>
                    <TableCol>{t('BOOKING_COM_RATE_ID')}</TableCol>
                    <TableCol>{t('BOOKING_COM_RATE_NAME')}</TableCol>
                    <TableCol width={100}>{t('ACTIONS')}</TableCol>
                </TableRow>

                <div className="body">
                    {loading
                        ? Array(4)
                              .fill(0)
                              .map((_, i) => (
                                  <TableRow key={i}>
                                      <TableCol>
                                          <Skeleton width={'100%'} animation="wave" height={35} />
                                      </TableCol>
                                  </TableRow>
                              ))
                        : bookingComRates?.map((item, index) => (
                              <TableRow key={item.id}>
                                  <TableCol width={30}>{index + 1}</TableCol>
                                  <TableCol>{get(connectedRates, [item.id, 'name'], '-')}</TableCol>
                                  <TableCol>{item.id || '-'}</TableCol>
                                  <TableCol>{item.name || '-'}</TableCol>
                                  <TableCol width={100}>
                                      <IconButton
                                          onClick={() => {
                                              handleShow();
                                              setActiveBookingComRate(item);
                                          }}
                                      >
                                          <EditIcon />
                                      </IconButton>
                                      <IconButton
                                          disabled={!get(connectedRates, [item.id, 'id'])}
                                          onClick={() => handleDelete(get(connectedRates, [item.id, 'id']))}
                                      >
                                          <TrashIcon />
                                      </IconButton>
                                  </TableCol>
                              </TableRow>
                          ))}
                </div>
            </div>

            <SelectRateModal isOpen={isOpen} onClose={hide} onSuccess={fetchData} rates={notConnectedRates} bookingComRate={activeBookingComRate} />
        </div>
    );
};

const SelectRateModal = ({ isOpen, onClose, onSuccess, rates, bookingComRate }) => {
    const { t } = useContext(LanguageContext);
    const [updating, setUpdating] = useState(false);

    const { handleSubmit, control, reset } = useForm({ defaultValues: { rateId: null } });

    const onSubmit = (data) => {
        setUpdating(true);
        request({
            url: `/booking-com/rate`,
            method: 'put',
            params: { ...data, bookingComRateId: bookingComRate.id, bookingComRateName: bookingComRate.name },
            success: () => {
                toast.success(t('RATE_UPDATED_SUCCESSFULLY'));
                reset({ rateId: null });
                onClose();
                onSuccess();
            },
            fail: handleError,
            finish: () => {
                setUpdating(false);
            },
        });
    };

    return (
        <StyledModal
            active={isOpen}
            onClose={() => {
                reset({ rateId: null });
                onClose();
            }}
        >
            <div className="modal-content">
                <h4>{t('SELECT_A_RATE')}</h4>

                <form className="modal-content-body" onSubmit={handleSubmit(onSubmit)}>
                    <MySelect nullable={false} control={control} name="rateId" options={rates} sx={{ width: '100%' }} placeholder={t('RATE')} />

                    <div className="modal-content-footer">
                        <Button loading={updating} color="primary" type="submit">
                            {t('SAVE')}
                        </Button>
                    </div>
                </form>
            </div>
        </StyledModal>
    );
};

export default RatesIntegration;
